import { AddAPhoto, HideImage } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormHelperText, Grid } from '@mui/material'
import { GreenText } from 'components'
import { MichiganSOSBundleTitleApplicationDeliveryPhotoDto, michiganSOSBundleTitleApplicationDeliveryPhotoValidationSchema } from 'dtos'
import { Form, Formik } from 'formik'
import { useEffect, useState } from 'react'

interface TitleApplicationDeliveryPhotoDialogProps {
  initialValues: MichiganSOSBundleTitleApplicationDeliveryPhotoDto
  isLoading: boolean
  onClose: () => void
  onSave: (values: MichiganSOSBundleTitleApplicationDeliveryPhotoDto) => void
  open: boolean
}

export default function TitleApplicationDeliveryPhotoDialog({ initialValues, isLoading, onClose, onSave, open }: TitleApplicationDeliveryPhotoDialogProps) {
  const [objectURL, _setObjectURL] = useState<string>('')

  useEffect(() => {
    if (initialValues.titleApplicationDeliveryPhotoFile) {
      setObjectURL(initialValues.titleApplicationDeliveryPhotoFile)
    }

    return () => {
      clearObjectURL()
    }
  }, [open, initialValues.titleApplicationDeliveryPhotoFile])

  function setObjectURL(file: File) {
    _setObjectURL(URL.createObjectURL(file))
  }

  function clearObjectURL() {
    if (objectURL) {
      URL.revokeObjectURL(objectURL)
      _setObjectURL('')
    }
  }

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
        validateOnBlur
        validateOnChange
        validationSchema={michiganSOSBundleTitleApplicationDeliveryPhotoValidationSchema}
      >
        {({ errors, isSubmitting, setFieldValue, values }) => (
          <Form>
            <DialogContent>
              <Grid container spacing={2} alignItems='center'>
                <DialogTitle>Title Application Delivery Photo</DialogTitle>

                <Grid item xs={12}>
                  <img alt='Title Application Delivery' src={objectURL} style={{ width: '100%' }} />

                  <input
                    type='file'
                    hidden
                    accept='.png,.jpg,.jpeg'
                    id='titleApplicationDeliveryPhotoFile'
                    onChange={e => {
                      if (e.target.files && e.target.files.length > 0) {
                        const file = e.target.files[0]

                        setFieldValue('titleApplicationDeliveryPhotoFile', file)
                        setObjectURL(file)
                      }
                    }}
                  />
                </Grid>

                {errors.titleApplicationDeliveryPhotoFile && (
                  <Grid item xs={12}>
                    <FormHelperText error>{errors.titleApplicationDeliveryPhotoFile}</FormHelperText>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <LoadingButton
                    color='primary'
                    disabled={isLoading || isSubmitting}
                    endIcon={<AddAPhoto />}
                    fullWidth
                    onClick={() => {
                      document.getElementById('titleApplicationDeliveryPhotoFile')?.click()
                    }}
                    variant='text'
                  >
                    BROWSE OR TAKE PHOTO
                  </LoadingButton>
                </Grid>

                <Grid item xs={12}>
                  <LoadingButton
                    color='error'
                    disabled={!values.titleApplicationDeliveryPhotoFile || isLoading || isSubmitting}
                    endIcon={<HideImage />}
                    fullWidth
                    onClick={() => {
                      setFieldValue('titleApplicationDeliveryPhotoFile', undefined)
                      clearObjectURL()
                    }}
                    variant='text'
                  >
                    DELETE UPLOADED PHOTO
                  </LoadingButton>
                </Grid>

                <Grid item xs={12}>
                  <GreenText variant='outlined'>
                    {`**Photo Previewer:**

Displays a preview of the uploaded photo.

If no photo is uploaded, nothing will be displayed.

**BROWSE OR TAKE PHOTO Button:**

Clicking this button opens the user’s camera app, allowing them to:
   - Take a new photo and upload it.
   - Upload an existing photo from their device.

**DELETE UPLOADED PHOTO Button:**

Clicking this button will:
   - Clear/delete the currently uploaded photo.
   - Remove it from the Photo Previewer.

**CANCEL Button:**

Clicking the CANCEL button will close the dialog and not save any changes

**SAVE Button:**

Clicking this button will:
   - Save the uploaded photo to the database and close the dialog`}
                  </GreenText>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button color='secondary' disabled={isLoading || isSubmitting} onClick={onClose} variant='text'>
                CANCEL
              </Button>

              <LoadingButton color='primary' loading={isLoading || isSubmitting} type='submit' variant='contained'>
                SAVE
              </LoadingButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}
